import type { ReactNode } from 'react'
import type { MessageKind, MessageStatus } from '@/types/clinic'

import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { CheckCheck, ExternalLink, Info, MessageCircle, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useClinic } from '@/lib/clinic/store'
import { formatCurrency, isValidPhone, normalizePhone } from '@/lib/format'
import { getInvoiceSummary } from '@/lib/clinic/selectors'
import { buildTemplateValues, buildWhatsAppLink, fillTemplate, hasUnfilledPlaceholders } from '@/lib/clinic/whatsapp'
import { usePhoneFormatter } from '@/hooks/use-clinic-helpers'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import FormField from '@/components/app/form-field'
import SimpleSelect from '@/components/app/simple-select'
import PatientAvatar from '@/components/app/patient-avatar'
import { MessageStatusBadge } from '@/components/app/status-badge'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import { MESSAGE_KIND_LABELS, MESSAGE_TEMPLATE_KEYS } from '@/constants/clinic'

export interface WhatsAppOptions {
    patientId: string
    kind?: MessageKind
    appointmentId?: string | null
    followUpId?: string | null
    invoiceId?: string | null
    messageId?: string
}

interface WhatsAppComposerProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    options: WhatsAppOptions
}

const KIND_OPTIONS = [...MESSAGE_TEMPLATE_KEYS, 'custom' as const].map(kind => ({ value: kind, label: MESSAGE_KIND_LABELS[kind] }))

const WhatsAppComposer = ({ open, onOpenChange, options }: WhatsAppComposerProps): ReactNode => {
    const { data, today, actions } = useClinic()
    const formatPhone = usePhoneFormatter()
    const settings = data.settings
    const existingMessage = options.messageId ? data.messages.find(item => item.id === options.messageId) : undefined
    const patient = data.patients.find(item => item.id === (existingMessage?.patientId ?? options.patientId)) ?? null

    // Pick the most relevant record for each template when the caller did not pass one.
    const linked = useMemo(() => {
        const patientId = patient?.id ?? ''
        const appointmentId = existingMessage?.appointmentId ?? options.appointmentId
        const followUpId = existingMessage?.followUpId ?? options.followUpId
        const invoiceId = existingMessage?.invoiceId ?? options.invoiceId
        const appointment = appointmentId
            ? data.appointments.find(item => item.id === appointmentId) ?? null
            : data.appointments
                .filter(item => item.patientId === patientId && item.date >= today && ['scheduled', 'confirmed'].includes(item.status))
                .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time))[0] ?? null
        const followUp = followUpId
            ? data.followUps.find(item => item.id === followUpId) ?? null
            : data.followUps
                .filter(item => item.patientId === patientId && item.status === 'pending')
                .sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0] ?? null
        const invoice = invoiceId ? data.invoices.find(item => item.id === invoiceId) ?? null : null
        const outstanding = data.invoices
            .filter(item => item.patientId === patientId)
            .reduce((sum, item) => sum + getInvoiceSummary(item).balance, 0)
        return { appointment, followUp, invoice, outstanding }
    }, [data, patient?.id, existingMessage, options.appointmentId, options.followUpId, options.invoiceId, today])

    const buildBody = (kind: MessageKind): string => {
        if (kind === 'custom') return patient ? `Hello ${patient.name}, ` : ''
        const values = buildTemplateValues({ patient, appointment: linked.appointment, followUp: linked.followUp, invoice: linked.invoice, settings })
        if (!linked.invoice && linked.outstanding > 0) values['[Amount]'] = formatCurrency(linked.outstanding, settings.billing.currency)
        if (kind === 'followUpReminder' && linked.followUp) values['[Date]'] = buildTemplateValues({ patient, followUp: linked.followUp, settings })['[Date]']
        return fillTemplate(settings.whatsapp.templates[kind] ?? '', values)
    }

    const initialKind: MessageKind = existingMessage?.kind ?? options.kind ?? 'custom'
    const [kind, setKind] = useState<MessageKind>(initialKind)
    const [phone, setPhone] = useState(existingMessage?.phone ?? (linked.appointment?.phone || patient?.phone || ''))
    const [body, setBody] = useState(() => existingMessage?.body ?? buildBody(initialKind))
    const [touched, setTouched] = useState(false)

    const phoneValid = isValidPhone(phone)
    const unfilled = hasUnfilledPlaceholders(body)
    const ready = phoneValid && body.trim().length > 0 && !unfilled
    const status: MessageStatus = ready ? 'ready' : 'draft'
    const link = ready ? buildWhatsAppLink(phone, settings.whatsapp.countryCode, body.trim()) : ''

    const record = (nextStatus: MessageStatus) => {
        if (!patient) return
        const payload = {
            patientId: patient.id,
            phone: normalizePhone(phone),
            kind,
            body: body.trim(),
            status: nextStatus,
            appointmentId: kind === 'appointmentConfirmation' || kind === 'appointmentReminder' ? linked.appointment?.id ?? null : null,
            followUpId: kind === 'followUpReminder' ? linked.followUp?.id ?? null : null,
            invoiceId: kind === 'paymentReminder' ? linked.invoice?.id ?? null : null
        }
        if (existingMessage) actions.updateMessage(existingMessage.id, payload)
        else actions.logMessage(payload)
    }

    const handleKindChange = (value: string) => {
        const nextKind = value as MessageKind
        setKind(nextKind)
        setBody(buildBody(nextKind))
    }

    const handleSaveDraft = () => {
        record(status)
        toast.success(status === 'ready' ? 'Message saved as ready to send' : 'Draft saved', { description: 'Find it in the WhatsApp activity on the Follow-ups page.' })
        onOpenChange(false)
    }

    const handleOpened = () => {
        record('sent')
        toast.success('WhatsApp opened with your message', { description: 'Marked as sent (demo). Delivery is not tracked until the WhatsApp API is connected.' })
        onOpenChange(false)
    }

    const contextLine = (() => {
        if ((kind === 'appointmentConfirmation' || kind === 'appointmentReminder') && !linked.appointment) return 'This patient has no upcoming appointment, so the date and time are left blank.'
        if (kind === 'followUpReminder' && !linked.followUp) return 'This patient has no pending follow-up, so the date is left blank.'
        if (kind === 'paymentReminder' && !linked.invoice && linked.outstanding <= 0) return 'This patient has no pending payment.'
        return ''
    })()

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-3xl'>
                <DialogHeader>
                    <DialogTitle className='flex items-center gap-2'>
                        <MessageCircle className='size-5 text-emerald-600' aria-hidden='true' />
                        WhatsApp message
                    </DialogTitle>
                    <DialogDescription>
                        Choose a template, edit the text and open it in WhatsApp.
                    </DialogDescription>
                </DialogHeader>

                {!patient
                    ? <p className='text-sm text-muted-foreground'>This patient record is no longer available.</p>
                    : (
                        <div className='grid gap-5 md:grid-cols-[1fr_17rem]'>
                            <div className='grid content-start gap-4'>
                                <div className='flex items-center justify-between gap-3 rounded-lg border bg-muted/40 p-3'>
                                    <div className='flex min-w-0 items-center gap-3'>
                                        <PatientAvatar name={patient.name} />
                                        <div className='min-w-0'>
                                            <p className='truncate text-sm font-semibold'>{patient.name}</p>
                                            <p className='text-xs text-muted-foreground'>{patient.code}</p>
                                        </div>
                                    </div>
                                    <MessageStatusBadge status={status} />
                                </div>

                                <div className='grid gap-4 sm:grid-cols-2'>
                                    <FormField label='Template' htmlFor='whatsapp-template'>
                                        <SimpleSelect id='whatsapp-template' value={kind} onValueChange={handleKindChange} options={KIND_OPTIONS} />
                                    </FormField>

                                    <FormField
                                        label='WhatsApp number'
                                        htmlFor='whatsapp-phone'
                                        error={touched && !phoneValid ? 'Enter a valid 10 digit Indian mobile number.' : ''}
                                        hint={phoneValid ? formatPhone(phone) : undefined}
                                    >
                                        <Input
                                            id='whatsapp-phone'
                                            inputMode='tel'
                                            value={phone}
                                            onChange={event => setPhone(event.target.value)}
                                            onBlur={() => setTouched(true)}
                                            aria-invalid={touched && !phoneValid}
                                        />
                                    </FormField>
                                </div>

                                <FormField
                                    label='Message'
                                    htmlFor='whatsapp-body'
                                    error={unfilled ? 'Replace the words in square brackets before sending.' : ''}
                                    hint={`${body.trim().length} characters`}
                                >
                                    <Textarea
                                        id='whatsapp-body'
                                        value={body}
                                        onChange={event => setBody(event.target.value)}
                                        rows={6}
                                        className='min-h-36'
                                    />
                                </FormField>

                                {contextLine && (
                                    <p className='flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900'>
                                        <TriangleAlert className='mt-0.5 size-3.5 shrink-0' aria-hidden='true' />
                                        {contextLine}
                                    </p>
                                )}
                            </div>

                            <div className='grid content-start gap-2'>
                                <p className='text-[13px] font-medium text-foreground/90'>Preview</p>
                                <div className='overflow-hidden rounded-xl border shadow-card'>
                                    <div className='flex items-center gap-2 bg-[#008069] px-3 py-2.5 text-white'>
                                        <span className='flex size-8 items-center justify-center rounded-full bg-white/20 text-xs font-semibold'>
                                            {patient.name.charAt(0)}
                                        </span>
                                        <div className='min-w-0 leading-tight'>
                                            <p className='truncate text-sm font-medium'>{patient.name}</p>
                                            <p className='truncate text-[11px] text-white/80'>{phoneValid ? formatPhone(phone) : 'Number needed'}</p>
                                        </div>
                                    </div>
                                    <div className='min-h-56 bg-[#efeae2] p-3'>
                                        {body.trim()
                                            ? (
                                                <div className='ml-auto max-w-[92%] rounded-lg rounded-tr-none bg-[#d9fdd3] px-2.5 py-1.5 text-[13px] leading-snug text-[#111b21] shadow-sm'>
                                                    <p className='break-words whitespace-pre-wrap'>{body.trim()}</p>
                                                    <p className='mt-1 flex items-center justify-end gap-1 text-[10px] text-[#667781]'>
                                                        Now
                                                        <CheckCheck className='size-3.5' aria-hidden='true' />
                                                    </p>
                                                </div>
                                            )
                                            : <p className='pt-16 text-center text-xs text-[#667781]'>Your message will appear here</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                <p className='flex items-start gap-2 text-xs text-muted-foreground'>
                    <Info className='mt-0.5 size-3.5 shrink-0' aria-hidden='true' />
                    Demo mode: WhatsApp opens in a new tab with this message ready. Nothing is sent until you press send there, and delivery is not tracked. Automatic sending will use the Meta WhatsApp Cloud API later.
                </p>

                <DialogFooter>
                    <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button type='button' variant='secondary' onClick={handleSaveDraft} disabled={!patient || !body.trim()}>
                        Save draft
                    </Button>
                    {ready
                        ? (
                            <a
                                href={link}
                                target='_blank'
                                rel='noopener noreferrer'
                                onClick={handleOpened}
                                className={cn(buttonVariants(), 'bg-emerald-600 text-white hover:bg-emerald-700')}
                            >
                                <ExternalLink aria-hidden='true' />
                                Open in WhatsApp
                            </a>
                        )
                        : (
                            <Button type='button' disabled className='bg-emerald-600 text-white'>
                                <ExternalLink aria-hidden='true' />
                                Open in WhatsApp
                            </Button>
                        )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default WhatsAppComposer
